// Рендерът на една клетка по `meta.cell`.
//
// Справката описва КАКВО е стойността, не как се рисува — така 14-те справки
// изглеждат еднакво и една промяна в типографията се прави на едно място.
// Само Tailwind класове и семантични токени; никакъв литерален цвят.

import { useTranslation } from "react-i18next";

import { fmtEuro } from "../../utils/currency";
import {
  EM_DASH,
  formatDateTime,
  formatMinutes,
  formatNumber,
  formatPercent,
} from "./cellFormat";
import type {
  BarCellValue,
  CellOptions,
  CellTone,
  ChipCellValue,
  DotCellValue,
  TextCellValue,
} from "./types";

/** Продължителност над този праг се чете като „масата стоя дълго". */
const DEFAULT_WARN_MINUTES = 90;

const DOT_TONE: Record<CellTone, string> = {
  neutral: "bg-ghost",
  brand: "bg-brand",
  ok: "bg-dot-idle",
  warn: "bg-brand-deep",
  danger: "bg-danger",
  info: "bg-info",
};

/** Същият тон, но като контур — вариантът `outline` на точката. */
const DOT_RING: Record<CellTone, string> = {
  neutral: "border-ghost",
  brand: "border-brand",
  ok: "border-dot-idle",
  warn: "border-brand-deep",
  danger: "border-danger",
  info: "border-info",
};

const CHIP_TONE: Record<CellTone, string> = {
  neutral: "bg-fill text-body",
  brand: "bg-brand-tint text-brand-deep-ink",
  ok: "bg-ok-tint text-ok-deep",
  warn: "bg-brand-tint text-brand-deep-ink",
  danger: "bg-danger-tint text-danger-ink",
  info: "bg-info-tint text-info-deep",
};

const MONO_NUMBER = "font-pos-mono text-[12.5px] font-semibold tabular-nums";

type Props = {
  options: CellOptions;
  value: unknown;
  /** Максимумът в колоната — нужен само на `bar`, когато редът не го носи. */
  columnMax?: number;
};

export const DataCell = ({ options, value, columnMax }: Props) => {
  const { t } = useTranslation();

  switch (options.kind) {
    case "text": {
      const text = value as TextCellValue | string | null | undefined;
      const primary =
        typeof text === "string" ? text : (text?.primary ?? "");
      const secondary = typeof text === "string" ? null : text?.secondary;
      if (!primary) {
        return <span className="text-faint">{EM_DASH}</span>;
      }
      const tag = typeof text === "string" ? null : text?.tag;
      return (
        <span className="flex min-w-0 flex-col leading-[1.25]">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              title={options.fullTextOnHover ? primary : undefined}
              className="truncate text-[13px] font-medium text-ink"
            >
              {primary}
            </span>
            {tag?.label ? (
              <span
                className={`shrink-0 rounded-md px-1.5 py-[2px] text-[10.5px] font-bold leading-[normal] ${
                  CHIP_TONE[tag.tone]
                }`}
              >
                {tag.label}
              </span>
            ) : null}
          </span>
          {options.lines === 2 && secondary ? (
            <span className="truncate text-[11px] font-medium text-muted">
              {secondary}
            </span>
          ) : null}
        </span>
      );
    }

    case "num": {
      const num = value as number | null | undefined;
      return (
        <span className={`${MONO_NUMBER} text-ink`}>
          {formatNumber(num, options.decimals)}
        </span>
      );
    }

    case "money": {
      const amount = value as number | null | undefined;
      if (amount === null || amount === undefined) {
        return <span className={`${MONO_NUMBER} text-faint`}>{EM_DASH}</span>;
      }
      // Отстъпката и сторното са положителни числа в данните, но се четат като
      // отнета сума — рисуват се с минус и в danger. Нулата е „нищо не се е
      // случило" и нарочно е тиха, за да не шуми колоната.
      const isZero = Math.abs(amount) < 0.005;
      const weight = options.strong ? "font-bold" : "font-semibold";
      if (options.negative) {
        const tone =
          isZero && options.mutedZero !== false ? "text-faint" : "text-danger-ink";
        return (
          <span className={`${MONO_NUMBER} ${weight} ${tone}`}>
            {isZero ? fmtEuro(0) : `−${fmtEuro(amount)}`}
          </span>
        );
      }
      return (
        <span
          className={`${MONO_NUMBER} ${weight} ${
            isZero && options.mutedZero ? "text-faint" : "text-ink"
          }`}
        >
          {fmtEuro(amount)}
        </span>
      );
    }

    case "dt": {
      const raw = value as string | Date | null | undefined;
      const { time, date } = formatDateTime(
        raw,
        options.withDate === "always"
          ? true
          : options.withDate === "never"
            ? false
            : "auto",
      );
      if (time === EM_DASH) {
        return <span className="text-faint">{EM_DASH}</span>;
      }
      return (
        <span className="flex flex-col leading-[1.25]">
          <span className="font-pos-mono text-[12.5px] font-semibold tabular-nums text-ink">
            {time}
          </span>
          {date ? (
            <span className="text-[11px] font-medium text-muted">{date}</span>
          ) : null}
        </span>
      );
    }

    case "min": {
      const minutes = value as number | null | undefined;
      const warnAbove = options.warnAbove ?? DEFAULT_WARN_MINUTES;
      const isLong =
        minutes !== null && minutes !== undefined && minutes > warnAbove;
      return (
        <span
          className={`${MONO_NUMBER} ${isLong ? "text-danger-ink" : "text-ink"}`}
        >
          {formatMinutes(minutes, {
            hour: t("dataTable.hourShort"),
            minute: t("dataTable.minuteShort"),
          })}
        </span>
      );
    }

    case "dot": {
      const dot = value as DotCellValue | null | undefined;
      if (!dot) return <span className="text-faint">{EM_DASH}</span>;
      return (
        <span className="flex items-center gap-2">
          <span
            aria-hidden
            className={`size-[9px] shrink-0 rounded-full ${
              dot.outline
                ? `border-[1.5px] bg-transparent ${DOT_RING[dot.tone]}`
                : DOT_TONE[dot.tone]
            }`}
          />
          <span className="sr-only">{dot.label}</span>
        </span>
      );
    }

    case "chip": {
      const chip = value as ChipCellValue | null | undefined;
      if (!chip?.label) return <span className="text-faint">{EM_DASH}</span>;
      return (
        <span
          className={`inline-block rounded-md px-2 py-[3px] text-[11px] font-bold leading-[normal] ${
            CHIP_TONE[chip.tone]
          }`}
        >
          {chip.label}
        </span>
      );
    }

    case "pct": {
      const pct = value as number | null | undefined;
      return (
        <span className={`${MONO_NUMBER} text-ink`}>
          {formatPercent(pct, options.decimals ?? 1, options.trim)}
        </span>
      );
    }

    case "bar": {
      const bar = value as BarCellValue | number | null | undefined;
      const amount = typeof bar === "number" ? bar : (bar?.value ?? 0);
      const max =
        typeof bar === "number" ? (columnMax ?? 0) : (bar?.max ?? columnMax ?? 0);
      const share = max > 0 ? Math.min(100, (amount / max) * 100) : 0;
      return (
        <span className="flex items-center justify-end gap-2">
          <span className={`${MONO_NUMBER} text-ink`}>
            {typeof bar === "number" || !bar?.label
              ? formatPercent(share, 0)
              : bar.label}
          </span>
          <span className="h-1.5 w-[52px] shrink-0 overflow-hidden rounded-full bg-fill-2">
            {/* Ширината е известна чак по време на изпълнение (дял от данните) —
                единственият позволен inline style в тази клетка. */}
            <span
              className="block h-full rounded-full bg-brand"
              style={{ width: `${share}%` }}
            />
          </span>
        </span>
      );
    }

    default:
      return null;
  }
};
